import {format} from 'date-fns';
import type {RouterOutput} from '@common-web/lib/trpc';
import type {ListColumnKey} from '@common/groupPipeline/viewConfig';
import {formatDate} from '@internal/pages/group-pipeline/components/pipelineDisplay';
import {STATUS_DISPLAY} from '@internal/pages/group-pipeline/components/StatusIndicator';
import {VBA_STATUS_DISPLAY} from '@internal/pages/group-pipeline/components/VbaStatusIndicator';

type ExportRow = RouterOutput['internal']['groupPipeline']['exportRows'][number];

// Each list column becomes one or more CSV columns; group, IDs, tenant and VBA status always lead.
const CSV_COLUMNS: Record<ListColumnKey, [string, (row: ExportRow) => string][]> = {
  phase: [['Phase', (row) => row.phase]],
  currentStep: [['Current step', (row) => row.currentStep?.label ?? '']],
  status: [
    ['Status', (row) => (row.status ? STATUS_DISPLAY[row.status].label : '')],
    [
      'Step due',
      (row) => (row.statusContext?.dueAt ? format(row.statusContext.dueAt, 'MMM d, yyyy') : ''),
    ],
    ['Reason', (row) => row.statusContext?.offReason ?? ''],
  ],
  members: [['Members', (row) => String(row.members)]],
  // VBA dates are date-only, stored as UTC midnight.
  effective: [['Effective', (row) => (row.effectiveDate ? formatDate(row.effectiveDate) : '')]],
};

export function groupsCsvRecords(rows: ExportRow[], columns: ListColumnKey[]) {
  const picked: [string, (row: ExportRow) => string][] = [
    ['Group', (row) => row.name],
    ['VBA group ID', (row) => row.vbaGroupId],
    ['Agent ID', (row) => row.admin123AgentId ?? ''],
    ['Tenant', (row) => row.tenantName ?? ''],
    ['VBA status', (row) => VBA_STATUS_DISPLAY[row.vbaStatus].label],
    ...columns.flatMap((key) => CSV_COLUMNS[key]),
  ];
  return {
    headers: picked.map(([header]) => header),
    records: rows.map((row) =>
      Object.fromEntries(picked.map(([header, value]) => [header, value(row)])),
    ),
  };
}
